import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Auto-grows a native textarea to fit its wrapped content (no scrollbar, no
 * resize handle — pair with `resize: none; overflow: hidden` in CSS).
 *
 * A native textarea (rather than ion-textarea's autoGrow) keeps the element
 * styleable exactly like the plain inputs around it — ion-textarea wraps the
 * control in its own shadow layout, which floats the text above the underline
 * and paints Ionic's green "valid" highlight on focus.
 *
 * Also resizes on programmatic form writes (patchValue on load, undo), which
 * fire no `input` event, and when the field's width changes (scale-to-fit /
 * orientation) so wrapping is remeasured.
 */
@Directive({
  selector: 'textarea[appAutoGrow]',
})
export class AutoGrowDirective implements AfterViewInit, OnDestroy {
  private value_changes?: Subscription;
  private width_observer?: ResizeObserver;
  private last_width = -1;

  constructor(private el: ElementRef<HTMLTextAreaElement>, @Optional() private control: NgControl) {}

  ngAfterViewInit(): void {
    this.resize();
    // setTimeout: run after Angular has written the new value into the DOM.
    this.value_changes = this.control?.valueChanges?.subscribe(() => setTimeout(() => this.resize()));
    // Width changes (appScaleToFit, rotation) change wrapping without an input event.
    // Ignore height-only notifications from our own resize() writes.
    if (typeof ResizeObserver !== 'undefined') {
      this.width_observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? 0;
        if (width === this.last_width) return;
        this.last_width = width;
        this.resize();
      });
      this.width_observer.observe(this.el.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.value_changes?.unsubscribe();
    this.width_observer?.disconnect();
  }

  @HostListener('input')
  onInput(): void {
    this.resize();
  }

  private resize(): void {
    const textarea = this.el.nativeElement;
    // Collapse both height and min-height for the measure. With overflow:hidden,
    // WebKit reports scrollHeight === clientHeight while CSS min-height still
    // clamps the box — so a lone `height:0` never sees wrapped lines and the
    // field stays one line tall. Clearing min-height lets scrollHeight reflect
    // the full content; the stylesheet min-height returns when we restore it.
    textarea.style.minHeight = '0';
    textarea.style.height = '0';
    const next = textarea.scrollHeight;
    textarea.style.minHeight = '';
    textarea.style.height = `${next}px`;
  }
}
